/**
 * NetworkOutputs string virtual views: four de-prefixed, ancestry-enriched
 * surfaces fronting the ~39-column `NetworkOutputs_String_Agent` source
 * (calculated/matched network string outputs, string-grain, StringContext).
 *
 * The source carries the GAP network model's per-string solve keyed by the
 * `wellString` producing-string relation. Every measure column is a per-run
 * timeseries handle: calculated production rates + pressures + injection, match
 * indices, the oil-only well-solver calibration, per-string flow-assurance/
 * constraint flags, and the gas-lift injection outputs. Each view here slices
 * one concern, de-prefixes to canonical names via the shared shapes, aligns the
 * handles into one row per (string × run timestamp) with a typed value column
 * per measure (units intact) plus the aligned `timestamp`, and enriches each row
 * with its well / field / reservoir ancestry resolved through the FlatHierarchy
 * registry.
 *
 * Unlike the sibling StringOutputs* views (which de-prefix a uniform `nmv`
 * axis off the DIFFERENT `StringOutputs_Network_Agent` source), this source has
 * NO `nmv` columns: its measure family is the `calculatedWell*` / `calculated*`
 * prefix plus assorted bare / `_well`-suffixed columns. The oil/gas/water phase
 * rates reuse the `PHASE_RATES` SSOT with the source's `calculatedWell` prefix
 * baked into each suffix; every other concern is authored as a bespoke
 * verbatim-suffix shape in `shapes.ts` (NET_STRING_*) so each measure's
 * `suffix === source column`.
 *
 * Built per session (not module-level) because `createAlignedSeriesView` closes
 * over the CDF client + hierarchy/entity views to custom-fetch and resolve
 * ancestry.
 */

import { createAlignedSeriesView } from '../createAlignedSeriesView.ts';
import type { VirtualView } from '../createReshapedView.ts';
import { describeAncestry, STRING_CONTEXT_ENRICH } from '../enrichAncestors.ts';
import type { Column, EnrichGroup, FlatColumn, Shape } from '../shapes/index.ts';
import {
  isStructColumn,
  NET_STRING_CALC_EXTRAS,
  NET_STRING_FLOW_ASSURANCE_FLAGS,
  NET_STRING_GAS_LIFT,
  NET_STRING_WELL_SOLVER_OIL,
  PHASE_RATES,
  STRING_KEEP,
} from '../shapes/index.ts';
import { useCatalogEnrichContext } from './useCatalogEnrichContext.ts';
import type { ViewFamilySpec } from './viewFamilySpec.ts';
import { NetworkOutputsStringAgentView } from '#schema/generated/ipso/views.ts';

/**
 * The oil/gas/water phase rates as they appear on `NetworkOutputs_String_Agent`:
 * the canonical `PHASE_RATES` SSOT with the source's `calculatedWell` prefix
 * baked into each suffix, so they sit in the same verbatim (`prefix:''`) group
 * as `NET_STRING_CALC_EXTRAS` (the aligned factory takes ONE measure group).
 */
const CALCULATED_PHASE_RATES: Shape = PHASE_RATES.flatMap((c: Column): readonly FlatColumn[] =>
  isStructColumn(c) ? [] : [{ ...c, suffix: `calculatedWell${c.suffix}` }],
);

/** One concern-scoped NetworkOutputs string surface: a canonical name, agent doc, and the de-prefixed measure group to keep. */
type NetworkOutputsStringSpec = ViewFamilySpec<EnrichGroup> & { readonly groups: EnrichGroup };

/**
 * The four NetworkOutputs string subviews, declared as data. Each de-prefixes
 * one concern of the network-solve measure family; all share identity + the
 * wellString relation and the well/field/reservoir ancestry enrichment. Every
 * measure column on this source is a per-run timeseries handle, so each subview
 * aligns into one row per (string × run timestamp) with typed per-measure value
 * columns (units intact) plus the aligned `timestamp` column.
 */
const NETWORK_OUTPUTS_STRING_SPECS: readonly NetworkOutputsStringSpec[] = [
  {
    name: 'StringCalculatedRates',
    description: `Per-string network-solved production/injection rates, pressures, match indices and equipment outputs (oil/gas/water/liquid rate, FBHP/FWHP/BHP, gas/water-injection rate & BHP, ESP motor load/frequency/efficiency, choke bean/opening/DP, pump liquid rate, power, formation GOR, match indices)${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    groups: { shape: [...CALCULATED_PHASE_RATES, ...NET_STRING_CALC_EXTRAS] },
  },
  {
    name: 'StringFlowAssuranceFlagsCalc',
    description: `Per-string flow-assurance condition flags (asphaltene, hydrate, scale, wax, PE5 stability) plus the operating-constraint flag from the network solve${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    groups: { shape: NET_STRING_FLOW_ASSURANCE_FLAGS },
  },
  {
    name: 'StringWellSolverMatchCalc',
    description: `Per-string well-solver oil-rate calibration: measured vs estimated oil rate and their difference${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    groups: { shape: NET_STRING_WELL_SOLVER_OIL },
  },
  {
    name: 'StringGasLift',
    description: `Per-string gas-lift injection outputs from the network solve (injection rate, injection pressure, calculated gas-lift rate)${describeAncestry(STRING_CONTEXT_ENRICH.relations)}`,
    groups: { shape: NET_STRING_GAS_LIFT },
  },
];

/**
 * Build the four de-prefixed, ancestry-enriched NetworkOutputs string surfaces.
 * Each is one `createAlignedSeriesView` over the shared source + enrichment:
 * one row per (string × run timestamp) with typed per-measure value columns and
 * the aligned `timestamp`. Filter by wellString (or join WellEntity) to scope by
 * hierarchy.
 */
export const useNetworkOutputsStringViews = (): readonly VirtualView[] => {
  const { client, mintSeries } = useCatalogEnrichContext();
  return NETWORK_OUTPUTS_STRING_SPECS.map((spec: NetworkOutputsStringSpec) =>
    createAlignedSeriesView({
      name: spec.name,
      description: spec.description,
      source: NetworkOutputsStringAgentView,
      keep: STRING_KEEP,
      groups: spec.groups,
      enrich: STRING_CONTEXT_ENRICH,
      client,
      mintSeries,
    }),
  );
};
